/**
 * Conversation Persistence with IndexedDB
 * Auto-save conversations and restore on reload
 */

const DB_NAME = 'unified-dev-agent';
const DB_VERSION = 1;
const CONVERSATIONS_STORE = 'conversations';
const MESSAGES_STORE = 'messages';
const SETTINGS_STORE = 'settings';

class PersistenceManager {
  constructor() {
    this.db = null;
    this.initialized = false;
  }

  /**
   * Initialize IndexedDB
   */
  async initialize() {
    if (this.initialized) return;

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        console.error('[Persistence] Failed to open DB:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        this.initialized = true;
        console.log('[Persistence] ✅ Database initialized');
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;

        // Conversations store
        if (!db.objectStoreNames.contains(CONVERSATIONS_STORE)) {
          const conversationsStore = db.createObjectStore(CONVERSATIONS_STORE, { keyPath: 'id' });
          conversationsStore.createIndex('timestamp', 'timestamp', { unique: false });
          conversationsStore.createIndex('title', 'title', { unique: false });
        }

        // Messages store
        if (!db.objectStoreNames.contains(MESSAGES_STORE)) {
          const messagesStore = db.createObjectStore(MESSAGES_STORE, { keyPath: 'id' });
          messagesStore.createIndex('conversationId', 'conversationId', { unique: false });
          messagesStore.createIndex('timestamp', 'timestamp', { unique: false });
        }

        // Settings store
        if (!db.objectStoreNames.contains(SETTINGS_STORE)) {
          db.createObjectStore(SETTINGS_STORE, { keyPath: 'key' });
        }

        console.log('[Persistence] Database schema created');
      };
    });
  }

  /**
   * Save conversation
   */
  async saveConversation(conversation) {
    await this.ensureInitialized();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([CONVERSATIONS_STORE], 'readwrite');
      const store = transaction.objectStore(CONVERSATIONS_STORE);

      const request = store.put({
        ...conversation,
        lastModified: Date.now()
      });

      request.onsuccess = () => resolve(true);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Save message
   */
  async saveMessage(message) {
    await this.ensureInitialized();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([MESSAGES_STORE], 'readwrite');
      const store = transaction.objectStore(MESSAGES_STORE);

      const request = store.put(message);

      request.onsuccess = () => resolve(true);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get all conversations
   */
  async getAllConversations() {
    await this.ensureInitialized();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([CONVERSATIONS_STORE], 'readonly');
      const store = transaction.objectStore(CONVERSATIONS_STORE);
      const index = store.index('timestamp');

      const request = index.openCursor(null, 'prev'); // Newest first
      const conversations = [];

      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          conversations.push(cursor.value);
          cursor.continue();
        } else {
          resolve(conversations);
        }
      };

      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get conversation by ID
   */
  async getConversation(id) {
    await this.ensureInitialized();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([CONVERSATIONS_STORE], 'readonly');
      const store = transaction.objectStore(CONVERSATIONS_STORE);
      const request = store.get(id);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get messages for conversation
   */
  async getMessages(conversationId) {
    await this.ensureInitialized();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([MESSAGES_STORE], 'readonly');
      const store = transaction.objectStore(MESSAGES_STORE);
      const index = store.index('conversationId');

      const request = index.getAll(conversationId);

      request.onsuccess = () => {
        const messages = request.result;
        messages.sort((a, b) => a.timestamp - b.timestamp); // Oldest first
        resolve(messages);
      };

      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Delete conversation and its messages
   */
  async deleteConversation(id) {
    await this.ensureInitialized();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(
        [CONVERSATIONS_STORE, MESSAGES_STORE],
        'readwrite'
      );

      // Delete conversation
      const conversationsStore = transaction.objectStore(CONVERSATIONS_STORE);
      conversationsStore.delete(id);

      // Delete all messages
      const messagesStore = transaction.objectStore(MESSAGES_STORE);
      const index = messagesStore.index('conversationId');
      const request = index.openCursor(IDBKeyRange.only(id));

      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          cursor.delete();
          cursor.continue();
        }
      };

      transaction.oncomplete = () => resolve(true);
      transaction.onerror = () => reject(transaction.error);
    });
  }

  /**
   * Save setting
   */
  async saveSetting(key, value) {
    await this.ensureInitialized();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([SETTINGS_STORE], 'readwrite');
      const store = transaction.objectStore(SETTINGS_STORE);

      const request = store.put({ key, value, timestamp: Date.now() });

      request.onsuccess = () => resolve(true);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get setting
   */
  async getSetting(key) {
    await this.ensureInitialized();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([SETTINGS_STORE], 'readonly');
      const store = transaction.objectStore(SETTINGS_STORE);
      const request = store.get(key);

      request.onsuccess = () => resolve(request.result?.value);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Ensure DB is initialized
   */
  async ensureInitialized() {
    if (!this.initialized) {
      await this.initialize();
    }
  }

  /**
   * Clear all data
   */
  async clearAll() {
    await this.ensureInitialized();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(
        [CONVERSATIONS_STORE, MESSAGES_STORE, SETTINGS_STORE],
        'readwrite'
      );

      transaction.objectStore(CONVERSATIONS_STORE).clear();
      transaction.objectStore(MESSAGES_STORE).clear();
      transaction.objectStore(SETTINGS_STORE).clear();

      transaction.oncomplete = () => {
        console.log('[Persistence] All data cleared');
        resolve(true);
      };
      transaction.onerror = () => reject(transaction.error);
    });
  }

  /**
   * Get database stats
   */
  async getStats() {
    await this.ensureInitialized();

    const conversations = await this.getAllConversations();

    let totalMessages = 0;
    for (const conv of conversations) {
      const messages = await this.getMessages(conv.id);
      totalMessages += messages.length;
    }

    return {
      conversations: conversations.length,
      messages: totalMessages,
      oldestConversation: conversations[conversations.length - 1]?.timestamp,
      newestConversation: conversations[0]?.timestamp
    };
  }
}

// Singleton instance
let persistenceManager = null;

export function getPersistenceManager() {
  if (!persistenceManager) {
    persistenceManager = new PersistenceManager();
  }
  return persistenceManager;
}

export default PersistenceManager;
